<template>
  <main
    v-if="container && freightOrders"
    class="row pt-3 mx-0"
  >
    <b-col
      cols="11"
      offset-lg="1"
      class="mb-4"
    >
      <h2>{{ $t('Container') }} {{ container.container }}</h2>
    </b-col>
    <b-col
      :lg="isContainerUpdate ? 7 : 10"
      offset-lg="1"
    >
      <form @submit.prevent="onSubmit">
        <b-row class="mb-4">
          <b-col>
            <dx-form-group :label="$t('shipping order')">
              <div
                id="link"
                @click="$router.push(`/freight-orders/${container.freight_order}`)"
                @mouseenter="isTooltipVisible = true"
                @mouseleave="isTooltipVisible = false"
              >
                <dx-select-box
                  v-model="container.freight_order"
                  :read-only="true"
                  :data-source="freightOrders"
                  value-expr="id"
                  :display-expr="freightOrdersDisplayExpr"
                  styling-mode="filled"
                />
              </div>
              <dx-tooltip
                :visible.sync="isTooltipVisible"
                target="#link"
                position="top"
              >
                {{ $t('to_freight_order') }}
              </dx-tooltip>
            </dx-form-group>
          </b-col>
        </b-row>
        <b-row class="mb-4">
          <b-col lk="9">
            <b-row>
              <b-col>
                <b-row>
                  <b-col cols="12">
                    <dx-fieldset-header :text="$t('Tracking status')" />
                  </b-col>
                  <b-col
                    v-for="it in statusFields"
                    :key="it.key"
                    lg="6"
                  >
                    <dx-form-group :label="it.label">
                      <dx-text-box
                        v-model="container[it.key]"
                        :read-only="true"
                        value-change-event="input"
                        styling-mode="filled"
                        :max-length="1000"
                      />
                    </dx-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="mb-4">
          <b-col lk="9">
            <dx-fieldset-header :text="$t('Location')" />
            <b-row>
              <b-col lg="9">
                <dx-form-group :label="$t('Location')">
                  <dx-text-box
                    v-model="container.location"
                    :read-only="!user.is_manager"
                    value-change-event="input"
                    styling-mode="filled"
                    :max-length="1000"
                  />
                </dx-form-group>
              </b-col>
              <b-col lg="3">
                <dx-form-group :label="$t('location date')">
                  <dx-date-box
                    v-model="container.location_date"
                    :read-only="!user.is_manager"
                    date-serialization-format="yyyy-MM-dd"
                    type="date"
                    class="w-100"
                    styling-mode="filled"
                  />
                </dx-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="mb-4">
          <b-col>
            <b-row>
              <b-col lg="12">
                <dx-fieldset-header :text="$t('Railway transport')" />
              </b-col>
              <b-col
                v-for="it in railwayTransportFields"
                :key="it.key"
                lg="6"
              >
                <dx-form-group
                  :key="it.key"
                  :label="it.label"
                >
                  <dx-text-box
                    v-model="container[it.key]"
                    :read-only="!user.is_manager"
                    value-change-event="input"
                    styling-mode="filled"
                    :max-length="it.maxLength"
                  />
                </dx-form-group>
              </b-col>
              <b-col lg="12">
                <dx-form-group :label="$t('railway note')">
                  <dx-text-box
                    v-model="container.railway_note"
                    :read-only="!user.is_manager"
                    value-change-event="input"
                    styling-mode="filled"
                    :max-length="1000"
                  />
                </dx-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row class="mb-4">
          <b-col cols="12">
            <dx-fieldset-header :text="$t('Track')" />
          </b-col>
          <b-col
            v-for="it in trackFields"
            :key="it.key"
            lg="4"
            md="4"
          >
            <dx-form-group :label="it.label">
              <dx-date-box
                v-model="container[it.key]"
                :read-only="it.readOnly"
                date-serialization-format="yyyy-MM-dd"
                type="date"
                class="w-100"
                styling-mode="filled"
              />
            </dx-form-group>
          </b-col>
        </b-row>
        <dx-fieldset-header :text="$t('Package state')" />
        <b-row class="mb-4">
          <b-col lg="3">
            <dx-form-group :label="$t('package id')">
              <dx-number-box
                v-model="container.pkg_pid"
                :read-only="!user.is_manager"
                value-change-event="input"
                styling-mode="filled"
                :max-length="10"
                :step="0"
              />
            </dx-form-group>
            <dx-form-group :label="$t('package state')">
              <dx-select-box
                v-model="container.pkg_state"
                :read-only="true"
                :data-source="packageStatusLookup.dataSource"
                value-expr="id"
                display-expr="name"
                styling-mode="filled"
              />
            </dx-form-group>
          </b-col>
          <b-col lg="4">
            <dx-form-group :label="$t('package notify')">
              <dx-text-area
                v-model="container.pkg_message"
                :read-only="!user.is_manager"
                height="124"
                value-change-event="input"
                styling-mode="filled"
                :max-length="2000"
              />
            </dx-form-group>
          </b-col>
          <b-col lg="5">
            <dx-form-group :label="$t('labels from custom declaration system')">
              <dx-data-grid
                ref="labelGrid"
                :data-source="labels"
                :show-column-headers="false"
                :on-row-prepared="onLabelsRowPrepared"
              >
                <dx-column
                  data-field="name"
                  :allow-search="false"
                />
                <dx-column
                  data-field="create_date"
                  data-type="date"
                  :allow-search="false"
                />
              </dx-data-grid>
            </dx-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col lg="12">
            <dx-fieldset-header :text="$t('TD')" />
            <b-row>
              <b-col
                v-for="it in tdFields"
                :key="it.key"
                lg="4"
              >
                <dx-form-group :label="it.label">
                  <dx-date-box
                    v-model="container[it.key]"
                    :read-only="!user.is_manager"
                    date-serialization-format="yyyy-MM-dd"
                    type="date"
                    class="w-100"
                    styling-mode="filled"
                  />
                </dx-form-group>
              </b-col>
            </b-row>
            <b-row class="mb-4">
              <b-col
                lg="6"
                class="mb-4 mb-lg-0"
              >
                <dx-form-group
                  :label="$t('td number')"
                  :margin-bottom="false"
                >
                  <dx-text-box
                    v-model="container.td_number"
                    :read-only="!user.is_manager"
                    value-change-event="input"
                    styling-mode="filled"
                  />
                </dx-form-group>
              </b-col>
              <b-col
                v-if="isContainerUpdate"
                lg="4"
                class="dl-links d-flex justify-content-between align-self-end pb-1"
              >
<!--                <a-->
<!--                  href="#"-->
<!--                  @click.prevent="openFile(TD_XML_URL, getTdFileName('xml'))"-->
<!--                >-->
<!--                  <i class="icon dx-icon-download mr-1"></i>-->
<!--                  {{ $t('download as XML') }}-->
<!--                </a>-->
                <a
                  href="#"
                  @click.prevent="openFile(TD_PDF_URL, getTdFileName('pdf'))"
                >
                  <i class="ace-icon icon-pdf mr-1"></i>
                  {{ $t('download as PDF') }}
                </a>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <dx-form-group :label="$t('td control')">
                  <dx-text-area
                    v-model="container.td_control"
                    :read-only="!user.is_manager"
                    height="104"
                    value-change-event="input"
                    styling-mode="filled"
                  />
                </dx-form-group>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <b-row v-if="user.is_administrator">
          <b-col cols="12">
            <dx-fieldset-header :text="$t('Tracking status')" />
          </b-col>
          <b-col lg="4">
            <dx-form-group :label="$t('status')">
              <dx-select-box
                v-model="container.status"
                :read-only="!user.is_manager"
                :data-source="containerStatusLookup.dataSource"
                value-expr="id"
                display-expr="name"
                styling-mode="filled"
              />
            </dx-form-group>
          </b-col>
          <b-col lg="4">
            <dx-form-group :label="$t('start tracking')">
              <dx-date-box
                v-model="container.start_tracking_date"
                :read-only="!user.is_manager"
                date-serialization-format="yyyy-MM-dd"
                type="date"
                class="w-100"
                styling-mode="filled"
              />
            </dx-form-group>
          </b-col>
          <b-col lg="4">
            <dx-form-group :label="$t('tracking done')">
              <dx-check-box
                v-model="container.tracking_done"
                :read-only="!user.is_manager"
                class="mt-3"
              />
            </dx-form-group>
          </b-col>
        </b-row>

        <b-row
          v-if="user.is_manager"
          class="mt-4"
        >
          <b-col>
            <dx-button
              :text="$t('Save')"
              type="success"
              styling-mode="contained"
              class="custom-btn mr-2"
              :disabled="loading"
              @click="onSubmit"
            />
          </b-col>
        </b-row>
      </form>
    </b-col>
    <b-col
      v-if="isContainerUpdate && container.freight_order"
      lg="3"
    >
      <b-row class="mb-3">
        <b-col lg="12">
          <status-history
            :freight-order-id="container.freight_order"
            :container-id="id"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col lg="12">
          <dx-fieldset-header :text="$t('Files')" />
        </b-col>
        <b-col lg="12">
          <dx-data-grid
            ref="fileTable"
            class="files-grid"
            :data-source="files"
            :show-column-headers="false"
          >
            <dx-column
              data-field="url"
              :allow-search="false"
              cell-template="file-cell-template"
            />
            <template #file-cell-template="{ data }">
              <a
                href="#"
                @click.prevent="openFile(data.data.url, data.data.filename)"
              >
                {{ data.data.filename }}
              </a>
            </template>
          </dx-data-grid>
        </b-col>
      </b-row>
    </b-col>
  </main>
</template>

<script>
import { mapState } from 'vuex';

import { DxSelectBox, DxTextBox, DxNumberBox, DxDateBox, DxTextArea, DxButton, DxCheckBox } from 'devextreme-vue'; // eslint-disable-line
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';
import { DxTooltip } from 'devextreme-vue/tooltip';

import DxFormGroup from '@/components/common/DxFormGroup';
import DxFieldsetHeader from '@/components/common/DxFieldsetHeader';
import StatusHistory from '@/components/container/StatusHistory';

import createCustomStore from '@/utils/create-custom-store';
import createStatusLookup from '@/utils/create-status-lookup';
import createPackageStatusLookup from '@/utils/create-package-status-lookup';
import createContainerStatusLookup from '@/utils/create-container-status-lookup';

export default {
  name: 'Container',
  components: {
    DxTooltip,
    StatusHistory,
    DxSelectBox,
    DxTextBox,
    DxDateBox,
    DxTextArea,
    DxFormGroup,
    DxFieldsetHeader,
    DxButton,
    DxDataGrid,
    DxColumn,
    DxCheckBox,
    DxNumberBox,
  },
  props: {
    id: String,
  },
  data: vm => ({
    file: null,
    containerStatusApproved: 2,
    freightOrders: {
      store: createCustomStore('tracking/api/freight_orders/'),
      paginate: true,
    },
    loading: false,
    statusLookup: createStatusLookup(vm),
    packageStatusLookup: createPackageStatusLookup(vm),
    containerStatusLookup: createContainerStatusLookup(vm),
    container: {
      arrival_at_border_date: '',
      commercial_documents_upload_date: '',
      container: '',
      container_type: '',
      final_release_date: '',
      freight_order: null,
      location: '',
      location_date: '',
      prepare_to_ship_date: '',
      railway_accept_date: '',
      railway_loading_date: '',
      railway_note: '',
      railway_transport_number: '',
      railway_trip_finish_date: '',
      railway_trip_start_date: '',
      railwaybill_number: '',
      start_tracking_date: '',
      tracking_done: false,
      status: null,
    },
    files: null,
    labels: null,
    apiUrl: process.env.VUE_APP_API_URL,
    isTooltipVisible: false,
  }),
  computed: {
    ...mapState(['user']),
    isContainerUpdate() {
      return !!this.id;
    },
    TD_XML_URL() {
      return `/tracking/api/freight_orders/${this.container.freight_order}/containers/td/${this.container.id}/`;
    },
    TD_PDF_URL() {
      return `/tracking/api/freight_orders/${this.container.freight_order}/containers/td-pdf/${this.container.id}/`;
    },
    statusFields() {
      return [
        {
          date: 'port_freight_forwarding_date',
          key: 'port_freight_forwarding_status',
          label: this.$t('port freight forwarding status'),
        },
        {
          date: 'transit_decl_date',
          key: 'transit_decl_status',
          label: this.$t('transit decl status'),
        },
      ];
    },
    trackFields() {
      return [
        {
          key: 'prepare_to_ship_date',
          label: this.$t('prepare to ship date'),
          readOnly: !this.user.is_manager && this.container.status === this.containerStatusApproved,
        },
        {
          key: 'commercial_documents_upload_date',
          label: this.$t('commercial documents upload date'),
          readOnly: !this.user.is_manager,
        },
        {
          key: 'arrival_at_border_date',
          label: this.$t('arrival at border date'),
          readOnly: !this.user.is_manager,
        },
        {
          key: 'railway_accept_date',
          label: this.$t('railway accept date'),
          readOnly: !this.user.is_manager,
        },
        {
          key: 'railway_loading_date',
          label: this.$t('railway loading date'),
          readOnly: !this.user.is_manager,
        },
        {
          key: 'railway_trip_start_date',
          label: this.$t('railway trip start date'),
          readOnly: !this.user.is_manager,
        },
        {
          key: 'railway_trip_finish_date',
          label: this.$t('railway trip finish date'),
          readOnly: !this.user.is_manager,
        },
        {
          key: 'final_release_date',
          label: this.$t('final release date'),
          readOnly: !this.user.is_manager,
        },
      ];
    },
    railwayTransportFields() {
      return [
        {
          key: 'railway_transport_number',
          label: this.$t('railway transport number'),
          maxLength: 20,
        },
        {
          key: 'railwaybill_number',
          label: this.$t('railwaybill number'),
          maxLength: 20,
        },
      ];
    },
    tdFields() {
      return [
        {
          key: 'td_date',
          label: this.$t('td date'),
        },
        {
          key: 'td_allowed_date',
          label: this.$t('td allowed date'),
        },
        {
          key: 'td_end_date',
          label: this.$t('td end date'),
        },
      ];
    },
    dtFields() {
      return [
        {
          key: 'dt_date',
          label: this.$t('td date'),
        },
        {
          key: 'dt_allowed_date',
          label: this.$t('dt allowed date'),
        },
      ];
    },
  },
  async created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);

    if (!this.isContainerUpdate) {
      return;
    }

    await this.getContainer();
    this.getFiles();
    this.getLabels();
  },
  methods: {
    async getContainer() {
      this.container = await this.$http.get(`tracking/api/containers/${this.id}/`);
      return Promise.resolve();
    },
    async getFiles() {
      const url = `tracking/api/freight_orders/${this.container.freight_order}/containers/${this.id}/package-content/`;
      const { results } = await this.$http.get(url);

      this.files = results.map(file => {
        file.url = `/tracking/api/freight_orders/${file.freight_order}/file/${file.freight_order_file_id}`;
        return file;
      });
      this.files = results;
    },
    onSubmit() {
      if (this.isContainerUpdate) {
        this.editContainer();
        return;
      }

      this.addContainer();
    },
    async getLabels() {
      this.labels = await this.$http.get(`/tracking/api/freight_orders/${this.container.freight_order}/containers/${this.id}/labels/`);
    },
    async editContainer() {
      this.loading = true;
      try {
        await this.$http.patch(`tracking/api/containers/${this.id}/`, this.container);
      } finally {
        this.loading = false;
      }
    },
    async addContainer() {
      const container = {};
      Object.entries(this.container)
        .filter(([, value]) => value !== '' && value !== null)
        .forEach(([key, value]) => {
          container[key] = value;
        });
      await this.$http.post('tracking/api/containers/', container);
    },
    freightOrdersDisplayExpr(item) {
      if (!item) {
        return '';
      }
      return `N${item.id} ${item.company_name || this.$t('no company')} (${item.consignor || ''}-${item.consignee || ''})`;
    },
    openFile(url, filename) {
      this.$http({
        url: this.apiUrl + url,
        method: 'GET',
        responseType: 'blob',
      }).then(response => {
        const href = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    getTdFileName(type) {
      return this.container.td_number.split('/').join('_') + '.' + type;
    },
    onLabelsRowPrepared({ rowElement, data }) {
      rowElement.style = `background-color: ${data.color}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.ace-icon .icon-download {
  font-size: 20px;
}

.freight-link {
  display: block;
  width: fit-content;
  margin-top: -10px;
  margin-bottom: 15px;
}
</style>
